import { Controller } from 'stimulus';
import qs from 'qs';
import formSerialize from 'form-serialize';
import serializeFormToObject from '@/helpers/serializeFormToObject';
import {
  deleteFilterForButton,
  getDefaultForInput,
  getInputForKeyAndValue,
  getLabelTextForInput,
  renderSelectedFilter,
} from '@/helpers/ecommerce/filterHelpers';
import hasHistorySupport from '@/helpers/hasHistorySupport';
import createEvent from '@/helpers/createEvent';
import debounce from '@/helpers/debounce';
import jump from 'jump.js';

const $filtersToggle = $('.js-filters-toggle');
const $filtersCollapse = $('.js-filters-collapse');

class FiltersController extends Controller {
  connect() {
    this.filterDebounced = debounce(this.filter.bind(this), 250);

    this.onFilterChange();

    $filtersCollapse.on('shown.bs.collapse', () => {
      $filtersToggle.text($filtersToggle.data('open-text'));
    });

    $filtersCollapse.on('hidden.bs.collapse', () => {
      $filtersToggle.text($filtersToggle.data('close-text'));
    });
  }

  setPageNumber(page) {
    this.pageTarget.value = page;
  }

  onSearchInput({ target }) {
    this[target.name] = parseInt(target.value, 10);

    this.onFilterChange();
  }

  onLengthChange() {
    const lengthField = this.element.querySelector('[name="length_from"]');

    if (lengthField) { createEvent(lengthField, 'update:length'); }
  }

  onWidthChange() {
    const widthField = this.element.querySelector('[name="width_from"]');

    if (widthField) { createEvent(widthField, 'update:width'); }
  }

  onHeightChange() {
    const heightField = this.element.querySelector('[name="height_from"]');

    if (heightField) { createEvent(heightField, 'update:height'); }
  }

  onSearchBlur({ target }) {
    if (this[target.name] === parseInt(target.value, 10)) return;

    this[target.name] = parseInt(target.value, 10);

    this.onFilterChange();
  }

  onFilterChange(resetPageNumber = false) {
    if (resetPageNumber) {
      this.setPageNumber(1);
    }

    this.filterDebounced();
  }

  onResultsPerPageChange(e) {
    if (!this.hasResultsPerPageTarget) return;

    this.resultsPerPageTarget.value = e.target.value;

    this.filterDebounced();
  }

  onConfirmClick() {
    this.closeAllDropdowns();
  }

  onShowResults() {
    this.closeFilter();
  }

  closeFilter() {
    $(this.element.querySelector('#filter')).collapse('hide');
  }

  onWindowClick(event) {
    const { target } = event;
    const sectionElement = target.closest('.filter__section');

    if (!sectionElement) {
      this.closeAllDropdowns();
    }
  }

  closeAllDropdowns() {
    const dropdownNodes = this.element.querySelectorAll('.filter__section-collapse');
    const dropdowns = Array.prototype.slice.call(dropdownNodes);

    dropdowns.forEach((dropdown) => {
      $(dropdown).collapse('hide');
    });
  }

  onDeleteFilter(e) {
    deleteFilterForButton(e.target.closest('button'));
    this.onFilterChange();
  }

  getSelectedFilters() {
    const selectedValues = serializeFormToObject(this.formTarget);
    const selectedItems = [];

    selectedValues.forEach((val, key) => {
      if (!val) {
        return;
      }

      if (key === 'sort') {
        return;
      }

      const label = getLabelTextForInput(key, val);
      const input = getInputForKeyAndValue(key, val);
      const defaultValue = getDefaultForInput(input);

      if (input.value === defaultValue || !label) {
        return;
      }

      selectedItems.push({
        key,
        value: val,
        label,
      });
    });

    return selectedItems;
  }

  outputSelectedFilters(filters) {
    this.selectedFiltersOutputTarget.innerHTML = filters.map(renderSelectedFilter).join('');

    this.selectedFiltersTarget.style.display = filters.length > 0 ? 'block' : 'none';
  }

  filter() {
    this.emptyResults();
    this.toggleSpinner(true);

    this.outputSelectedFilters(this.getSelectedFilters());

    const parameters = formSerialize(this.formTarget, { hash: true });

    // Stringify the data object into a query string
    const stringified = qs.stringify(parameters, { encode: false });

    const queryString = stringified !== '' ? `?${stringified}` : '';

    if (hasHistorySupport()) {
      // Get the full base url without any parameters
      const baseUrl = [window.location.protocol, '//', window.location.host, window.location.pathname].join('');

      try {
        // Output the query string after the baseUrl as GET parameters
        window.history.replaceState({}, '', `${baseUrl}${queryString}`);
      } catch (error) {
        // console.error(error);
      }
    }

    $.post(`${this.formTarget.action}${queryString}`)
      .then((res) => {
        if (typeof res.products === 'undefined' || !this.hasResultsTarget) return;

        this.toggleSpinner(false);
        this.resultsTarget.innerHTML = res.products;

        if (typeof res.customBoxes !== 'undefined' && this.hasCustomBoxesTarget) {
          this.customBoxesTarget.innerHTML = res.customBoxes;
        }

        if (typeof res.boxSearchFormat !== 'undefined' && this.hasBoxSearchFormatTarget) {
          this.boxSearchFormatTarget.innerHTML = res.boxSearchFormat;
        }

        this.updateResultsCount(parseInt(res.count, 10));
      })
      .then(() => this.scrollToTop());
  }

  updateResultsCount(count) {
    this.resultsCountTargets.forEach((el) => {
      let text = count;

      if (typeof el.dataset.showSuffix !== 'undefined') {
        text = `${count} ${el.dataset[count === 1 ? 'resultsSingular' : 'resultsPlural']}`;
      }

      el.innerText = text;
    });
  }

  emptyResults() {
    if (!this.hasResultsTarget) return;

    this.resultsTarget.innerHTML = '';
  }

  toggleSpinner(state) {
    if (!this.hasSpinnerTarget) return;

    this.spinnerTarget.style.display = state ? 'block' : 'none';
  }

  scrollToTop() {
    jump(this.element, {
      offset: -100,
      a11y: false,
      duration: 1000,
    });
  }
}

FiltersController.targets = [
  'form',
  'page',
  'selectedFilters',
  'selectedFiltersOutput',
  'spinner',
  'sort',
  'results',
  'resultsCount',
  'resultsPerPage',
  'customBoxes',
  'boxSearchFormat',
  'rangeSliders',
  'width',
  'height',
  'length',
  'amount',
];

export default FiltersController;
